import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { useActiveWorld } from '../hooks/useActiveWorld';
import './ApiPendingBanner.css';

/**
 * Banner-Komponente die anzeigt, wenn API-Daten auf Review warten
 * Kann in Header, Dashboard oder WorldDb eingebunden werden
 */
const ApiPendingBanner = ({ onReviewClick, compact = false }) => {
  const { userId, currentUserId } = useActiveWorld();
  const isOwnWorld = !!(userId && currentUserId && userId === currentUserId);
  const navigate = useNavigate();
  const [pending, setPending] = useState({ batches: 0, contacts: 0 });
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const functions = getFunctions(undefined, 'europe-west1');

  useEffect(() => {
    checkPendingImports();
  }, [userId, currentUserId]);

  const checkPendingImports = async () => {
    if (!userId || !isOwnWorld) {
      setLoading(false);
      return;
    }

    try {
      const getStatus = httpsCallable(functions, 'getWorldDbApiKeyStatus');
      const result = await getStatus({ userId });
      
      if (result.data.pending) {
        setPending(result.data.pending);
      }
    } catch (err) {
      console.error('Error checking pending imports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewClick = () => {
    if (onReviewClick) {
      onReviewClick();
    } else {
      // Navigate to API Settings
      navigate('/world?view=settings&tab=api');
    }
  };

  if (loading || pending.batches === 0 || dismissed || !isOwnWorld) {
    return null;
  }

  if (compact) {
    return (
      <div className="api-pending-banner-compact" onClick={handleReviewClick}>
        <div className="api-pending-banner-dot"></div>
        <span>{pending.contacts} contacts waiting</span>
      </div>
    );
  }

  return (
    <div className="api-pending-banner">
      <div className="api-pending-banner-icon">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className="api-pending-banner-content">
        <strong>{pending.contacts.toLocaleString()} contacts</strong> received via API
        <span>{pending.batches} batch{pending.batches > 1 ? 'es' : ''} waiting for review</span>
      </div>
      <button className="api-pending-banner-btn" onClick={handleReviewClick}>
        Review & Import
      </button>
      <button 
        className="api-pending-banner-dismiss" 
        onClick={() => setDismissed(true)}
        title="Dismiss for now"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
};

export default ApiPendingBanner;
